import { useEffect, useRef } from 'react';

const tickerItems = [
  '✓ 24/7 AI Answering',
  '✓ Auto-Books Jobs',
  '✓ Tradify, Jobber & More',
  '✓ GDPR Compliant',
  '✓ WhatsApp & Instagram AI',
  '✓ Irish Support',
];

export default function Hero() {
  const heroRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('visible');
          }
        });
      },
      { threshold: 0.1 }
    );

    const reveals = heroRef.current?.querySelectorAll('.reveal');
    reveals?.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  const doubled = [...tickerItems, ...tickerItems];

  return (
    <section
      ref={heroRef}
      className="relative min-h-screen flex flex-col justify-center overflow-hidden pt-14"
      style={{ backgroundColor: '#080F0C' }}
    >
      {/* Animated Grid Mesh */}
      <div
        className="absolute inset-0 grid-mesh animate-grid-pulse pointer-events-none"
        aria-hidden="true"
      />

      {/* Radial glow */}
      <div className="absolute inset-0 radial-glow pointer-events-none" aria-hidden="true" />

      {/* Accent bottom glow */}
      <div
        className="absolute bottom-0 left-0 right-0 h-48 pointer-events-none"
        style={{ background: 'linear-gradient(to top, rgba(10,92,68,0.2), transparent)' }}
        aria-hidden="true"
      />

      {/* Content */}
      <div className="relative z-10 max-w-2xl mx-auto px-5 lg:px-8 pt-16 pb-8 w-full">
        {/* Pre-tag */}
        <div
          className="reveal mb-4 opacity-0"
          style={{ transitionDelay: '0ms' }}
        >
          <span
            className="font-dm font-semibold text-xs tracking-wider sm:tracking-[0.2em] uppercase"
            style={{ color: '#AAFF45', textShadow: '0 0 12px rgba(170,255,69,0.7)' }}
          >
            ◆ AI FOR IRISH &amp; UK TRADES BUSINESSES
          </span>
        </div>

        {/* Headline */}
        <h1
          className="reveal font-bebas text-white leading-none opacity-0 mb-4"
          style={{
            fontSize: 'clamp(48px, 15vw, 64px)',
            transitionDelay: '100ms',
            textShadow: '0 2px 30px rgba(0,0,0,0.5)',
          }}
        >
          Stop Missing Calls.<br />
          <span style={{ color: '#AAFF45', textShadow: '0 0 30px rgba(170,255,69,0.4)' }}>
            Start Booking Jobs.
          </span>
        </h1>

        {/* Subheadline */}
        <p
          className="reveal font-dm text-brand-grey opacity-0 mb-7 leading-relaxed"
          style={{ fontSize: '18px', transitionDelay: '200ms' }}
        >
          Our AI answers every call, qualifies leads, and books jobs — 24/7, while you're on the tools.
        </p>

        {/* Glowing Badge */}
        <div
          className="reveal opacity-0 mb-7"
          style={{ transitionDelay: '300ms' }}
        >
          <div
            className="animate-badge-pulse flex flex-wrap items-center gap-2 px-4 py-3 rounded-xl font-dm font-bold text-sm w-full sm:w-auto sm:inline-flex"
            style={{
              backgroundColor: '#AAFF45',
              color: '#080F0C',
              boxShadow: '0 0 20px rgba(170,255,69,0.5)',
            }}
          >
            <span className="flex-shrink-0">🔥</span>
            <span className="tracking-wide text-xs uppercase leading-snug">
              TRY FREE UNTIL YOU BOOK YOUR FIRST JOB
              <span className="hidden sm:inline"> · No card. No risk. No excuses.</span>
              <span className="sm:hidden block text-xs font-medium normal-case opacity-80">No card. No risk. No excuses.</span>
            </span>
          </div>
        </div>

        {/* Main CTA */}
        <div
          className="reveal opacity-0 mb-3"
          style={{ transitionDelay: '400ms' }}
        >
          <a
            href="https://apex-emerald-ai.com/contact"
            className="whitespace-nowrap block w-full text-center font-dm font-bold rounded-xl cursor-pointer transition-all duration-200 hover:brightness-110 active:scale-95"
            style={{
              backgroundColor: '#AAFF45',
              color: '#080F0C',
              fontSize: '20px',
              padding: '16px 24px',
              minHeight: '56px',
              boxShadow: '0 0 32px rgba(170,255,69,0.4), 0 4px 20px rgba(0,0,0,0.4)',
            }}
          >
            Start My Free Trial →
          </a>
        </div>

        {/* Below CTA */}
        <p
          className="reveal opacity-0 text-center font-dm text-brand-grey text-xs mb-8"
          style={{ transitionDelay: '480ms' }}
        >
          No payment needed. Cancel anytime. First job booked or it's free.
        </p>

        {/* Divider line */}
        <div
          className="w-full h-px mb-5"
          style={{ background: 'linear-gradient(to right, transparent, rgba(10,92,68,0.6), transparent)' }}
        />

        {/* Ticker */}
        <div className="ticker-wrap w-full -mx-5 px-0">
          <div
            className="ticker-inner animate-ticker gap-0"
            style={{ width: 'max-content' }}
          >
            {doubled.map((item, i) => (
              <span
                key={i}
                className="font-dm font-medium text-sm mx-4 whitespace-nowrap"
                style={{ color: '#A0A8A4' }}
              >
                {item}
              </span>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
